import type { Slice } from "@reduxjs/toolkit"
import {
  combineSlices,
  configureStore,
  createListenerMiddleware,
} from "@reduxjs/toolkit"
import {
  // eslint-disable-next-line @typescript-eslint/no-restricted-imports
  useDispatch as useDispatchBase,
  // eslint-disable-next-line @typescript-eslint/no-restricted-imports
  useSelector as useSelectorBase,
} from "react-redux"
import { setupListeners } from "@reduxjs/toolkit/query/react"
import { rememberEnhancer, rememberReducer } from "redux-remember"
import { prefsSlice } from "@holigral/shared/prefs"
import { clearAbly } from "./ably"
import baseApi from "./baseApi"
import authSlice, { getTenantSlug, getUserId } from "@/auth/slice"

export const rootReducer = combineSlices(baseApi, authSlice, prefsSlice)

export type RootState = ReturnType<typeof rootReducer>

const leaves = (before: string | null, after: string | null) =>
  before !== null && after !== before

// The API cache is keyed by neither user nor workspace, so it goes when either
// changes; RTK Query discards replies to requests sent before. A first user or
// workspace keeps it: UserRoute renders nothing until the store holds both, so
// nothing cached earlier belongs to either.
const cacheScope = createListenerMiddleware()
cacheScope.startListening.withTypes<RootState>()({
  predicate: (_action, current, previous) =>
    leaves(getUserId(previous), getUserId(current)) ||
    leaves(getTenantSlug(previous), getTenantSlug(current)),
  effect: (_action, { dispatch }) => {
    clearAbly()
    dispatch(baseApi.util.resetApiState())
  },
})

// Not checking immutability or serializability for anything by RTK Query
const ignoredPaths = ["api"]

const store = configureStore({
  reducer: rememberReducer(rootReducer),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: { ignoredPaths },
      serializableCheck: { ignoredPaths },
    })
      .prepend(cacheScope.middleware)
      .concat(baseApi.middleware),
  enhancers: (getDefaultEnhancers) => {
    const enhancers = getDefaultEnhancers()
    if (typeof window !== "undefined") {
      return enhancers.concat(rememberEnhancer(window.localStorage, ["prefs"]))
    }

    return enhancers
  },
})

setupListeners(store.dispatch)

type AppDispatch = typeof store.dispatch
export const useDispatch = useDispatchBase.withTypes<AppDispatch>()

export const useSelector = useSelectorBase.withTypes<RootState>()

export const injectSlice = <TSlice extends Slice>(slice: TSlice) => {
  rootReducer.inject(slice)

  // dummy dispatch to init slice
  store.dispatch({ type: `@@SLICE/${slice.name}` })

  return useSelectorBase.withTypes<
    RootState & { [TPath in TSlice["name"]]: ReturnType<TSlice["reducer"]> }
  >()
}

export default store
